import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { green, grey, red } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { AutomationsContext } from "contexts/Automations";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { BrandsContext } from "contexts/Brands";
import { OrganizationUserContext } from "contexts/Organization";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Automation,
  AutomationStatus,
  BentoBrandSearchParameters,
  BentoCategory,
  OrganizationNiche,
  OrganizationNicheStatus,
  SavedBrandCollection,
  SearchParams,
  Sort,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import {
  convertFilterParamsToQuery,
  isEqualParams,
} from "utils/discoverSearch";
import { daysOfWeek } from "utils/time";
import { makeDeepCopy } from "utils/updateLocalState";
import { useSearchBrand } from "utils/useSearchBrand";

import AutomationParams from "./AutomationParams";
import CreateNicheDialog from "./CreateNicheDialog";
import EditAutomationNicheDialog from "./EditAutomationNicheDialog";
import styles from "./styles";

interface Props {
  open: boolean;
  handleClose: () => void;
  closeBothDialogs: () => void;
  editAutomation?: Automation;
  niches: OrganizationNiche[];
  fetchOrganizationNiches: () => void;
}

const CreateAutomationDialog = ({
  open,
  handleClose,
  closeBothDialogs,
  editAutomation,
  niches,
  fetchOrganizationNiches,
}: Props) => {
  const { hasTags, submitBentoSearchParams } = useSearchBrand();
  const { automations, setAutomations } = useContext(AutomationsContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { collections, fetchCollections } = useContext(
    SavedBrandCollectionsContext,
  );
  const [searchParams] = useSearchParams();
  const { discoverFilterParams, selectedMetadataTags } =
    useContext(BrandsContext);
  const searchQuery = searchParams.get(SearchParams.QUERY_DISCOVER);
  const [currentSearchParams, setCurrentSearchParams] =
    useState<BentoBrandSearchParameters>({
      query: "",
      sort: Sort.Recommended,
    });

  const { topLevelCategories, categoriesById } = useContext(
    BentoCategoriesContext,
  );

  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(false);
  const [refreshDialogOpen, setRefreshDialogOpen] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [editNicheDialogOpen, setEditNicheDialogOpen] =
    useState<boolean>(false);
  const [createNicheDialogOpen, setCreateNicheDialogOpen] =
    useState<boolean>(false);
  const [deleteNicheDialogOpen, setDeleteNicheDialogOpen] =
    useState<boolean>(false);
  const [deleteNicheLoading, setDeleteNicheLoading] = useState<boolean>(false);
  const [categoriesToPickFrom, setCategoriesToPickFrom] = useState<
    BentoCategory[]
  >([]);

  const [automation, setAutomation] = useState<Automation>({
    count: 20,
    dayOfWeek: 4,
    options: {
      collection_option: "none",
      collection_id: 0,
    },
    searchParameters: {
      query: "",
      sort: Sort.Recommended,
    },
    isTopNiche: false,
    status: AutomationStatus.active,
  });

  useEffect(() => {
    const converted = convertFilterParamsToQuery(
      topLevelCategories,
      categoriesById,
      discoverFilterParams,
      selectedMetadataTags,
      searchQuery || "",
    );
    if (editAutomation) {
      setAutomation(editAutomation);
      setCurrentSearchParams(converted);
    } else {
      setAutomation((prev) => {
        const copy = makeDeepCopy(prev);
        copy["searchParameters"] = converted;
        return copy;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: CustomEvent) => {
    let { name, value } = e.target;

    if (name === "count" && (value < 0 || value > 100)) {
      setWarning("You can only pick between 0 or 100 brands");
    } else if (name === "count") {
      setWarning("");
    }

    if (name === "organizationNicheId" && value === "None") {
      value = null;
    }

    setAutomation((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeCollection = (e: CustomEvent) => {
    const { value } = e.target;
    let collectionOption: "none" | "new" | "existing" = "none";
    if (value === 0) {
      collectionOption = "none";
    } else if (value === -1) {
      collectionOption = "new";
    } else {
      collectionOption = "existing";
    }
    setAutomation((prev) => ({
      ...prev,
      options: {
        collection_id: value,
        collection_option: collectionOption,
      },
    }));
  };

  const handleSave = async () => {
    setLoading(true);

    const body = makeDeepCopy(automation);
    if (body?.options?.collection_id === -1) {
      body.options.collection_id = null;
    }
    if (body.searchParameters.category_ids) {
      body.searchParameters.category_ids =
        body.searchParameters.category_ids.map((v: string) => parseInt(v));
    }

    try {
      const res = await fetcherAuth(
        getToken,
        automation?.id
          ? `/api/organization/${currentOrg?.id}/automations/${automation.id}`
          : `/api/organization/${currentOrg?.id}/automations`,
        automation?.id ? "PUT" : "POST",
        {},
        body,
      );
      fetchOrganizationNiches();
      setAutomations((prev) => {
        const copy = makeDeepCopy(prev);
        const index = copy?.findIndex(
          (x: Automation) => x.id === res.automation?.id,
        );
        if (index === -1) {
          copy.push(res.automation);
        } else {
          copy[index] = res.automation;
        }
        return copy;
      });
      setAlert("Successfully updated your automation", "success");
      if (!automation?.id) {
        handleClose();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReplace = () => {
    setAutomation((prev) => ({
      ...prev,
      searchParameters: currentSearchParams,
    }));
  };

  const handleTopNicheToggle = (event: ChangeEvent<HTMLInputElement>) => {
    setAutomation((prev) => ({
      ...prev,
      isTopNiche: event.target.checked,
    }));
  };

  const handleStatusToggle = (event: ChangeEvent<HTMLInputElement>) => {
    setAutomation((prev) => ({
      ...prev,
      status: event.target.checked
        ? AutomationStatus.inactive
        : AutomationStatus.active,
    }));
  };

  const fetchAutomationEmailSetting = async (automationId: number) => {
    setLoadingTemplates(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/automations/${automationId}/email-setting`,
        "GET",
      );
      if (res.templates && res.templates?.length > 0) {
        setSelectedTemplates(res.templates);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSubmitSearch = (searchParameters: BentoBrandSearchParameters) => {
    submitBentoSearchParams(searchParameters);
    closeBothDialogs();
  };

  const openRefreshDialog = () => {
    setRefreshDialogOpen(true);
  };

  const handleRefreshAutomation = async () => {
    setDeleteLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/automations/${automation?.id}/refresh`,
        "POST",
      );
      setAlert(
        "Successfully deleted saved brands associated with this automation.",
        "success",
      );
      fetchCollections();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getCategoriesToPickFrom = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-categories/onboarding?for_va=${true}`,
      );
      setCategoriesToPickFrom(res.onboardingBentoCategories);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleNicheUpdated = (updatedNiche: OrganizationNiche) => {
    fetchOrganizationNiches();
    setAlert("Niche updated successfully", "success");
  };

  const handleNicheCreated = (newNiche: OrganizationNiche) => {
    fetchOrganizationNiches();
    // Automatically set the newly created niche as the selected niche
    setAutomation((prev) => ({
      ...prev,
      organizationNicheId: newNiche.id,
    }));
    setAlert("Niche created successfully", "success");
  };

  const handleDeleteNiche = async () => {
    if (!automation.organizationNicheId) return;

    setDeleteNicheLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/${automation.organizationNicheId}`,
        "DELETE",
      );

      setAutomation((prev) => ({
        ...prev,
        organizationNicheId: undefined,
      }));

      fetchOrganizationNiches();
      setAlert("Niche deleted successfully", "success");
      setDeleteNicheDialogOpen(false);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setDeleteNicheLoading(false);
    }
  };

  useEffect(() => {
    if (editAutomation && automation?.id) {
      fetchAutomationEmailSetting(automation.id);
    } else if (!editAutomation) {
      if (automations.length > 0) {
        const firstAutomation = automations[0];
        if (firstAutomation?.id) {
          fetchAutomationEmailSetting(firstAutomation?.id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAutomation, automation?.id]);

  useEffect(() => {
    if (open && categoriesToPickFrom.length === 0) {
      getCategoriesToPickFrom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedCollectionId =
    automation.options.collection_id === undefined ||
    automation.options.collection_id === null
      ? -1
      : automation.options.collection_id;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobileScreen}
    >
      <DialogTitle>
        <IconButton sx={styles.icon} onClick={handleClose}>
          <Box component="i" className="fa-regular fa-arrow-left" />
        </IconButton>
        {automation?.id ? "Edit Automation" : "Create Automated Search"}
      </DialogTitle>

      <DialogContent>
        Bento will generate a list of brands based on these customizations.{" "}
        <strong>
          Once an automation is created, you cannot change its purpose (i.e. the
          niche that it is focused on)
        </strong>
        . If you want to change its niche, please pause the automation and
        create a new one.
        <Typography sx={{ mt: 2, fontSize: 16 }}>
          <strong>Set Customizations</strong>
        </Typography>
        <Grid container item xs={12} sx={{ my: 1 }} gap={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl variant="standard" fullWidth>
              <FormHelperText>Run this search on: </FormHelperText>
              <Select
                onChange={handleChange}
                label=""
                value={automation.dayOfWeek}
                name="dayOfWeek"
              >
                {daysOfWeek?.map((option: string, index: number) => (
                  <MenuItem key={index} value={index}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md>
            <FormHelperText error={!!warning}>
              Number of brands to find:{" "}
            </FormHelperText>
            <TextField
              variant="standard"
              type="number"
              size="small"
              value={automation.count}
              fullWidth
              name="count"
              onChange={handleChange}
            />
            <FormHelperText sx={{ color: "error.main" }}>
              {warning}
            </FormHelperText>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={6}>
            <FormControl variant="standard" fullWidth>
              <FormHelperText>Store results on: </FormHelperText>
              <Select
                label=""
                value={selectedCollectionId}
                onChange={handleChangeCollection}
              >
                {Object.values(collections).map(
                  (option: SavedBrandCollection, index: number) => (
                    <MenuItem key={index} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ),
                )}
                <MenuItem value={0}>All Saved</MenuItem>
                <MenuItem value={-1}>
                  <em>New Collection (to be created)</em>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} paddingLeft={4}>
            <FormControl variant="standard">
              <FormHelperText>Is top niche: </FormHelperText>{" "}
              <Switch
                checked={automation.isTopNiche}
                onChange={handleTopNicheToggle}
                color="primary"
              />
            </FormControl>
          </Grid>
        </Grid>
        <AutomationParams
          categoriesById={categoriesById}
          searchParameters={automation.searchParameters}
          handleReplace={handleReplace}
          handleSubmitSearch={handleSubmitSearch}
        />
        {(searchQuery || hasTags) &&
          automation?.id &&
          automation?.id > 0 &&
          !isEqualParams(automation.searchParameters, currentSearchParams) && (
            <AutomationParams
              categoriesById={categoriesById}
              searchParameters={currentSearchParams}
              isDashboardSearch={true}
              handleReplace={handleReplace}
              handleSubmitSearch={handleSubmitSearch}
            />
          )}
        <Typography sx={{ mt: 2, mb: 1, fontSize: 16 }}>
          <strong>Templates for this automation: </strong>
        </Typography>
        {loadingTemplates ? (
          <Box>
            <Skeleton height="60px" width="75%" />
            <Skeleton height="40px" width="75%" />
            <Skeleton height="40px" width="75%" />
          </Box>
        ) : (
          <Box>
            {selectedTemplates.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {selectedTemplates.map((template) => (
                  <Chip
                    key={template.id}
                    label={template.name}
                    variant="outlined"
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No templates selected.
              </Typography>
            )}
            <Alert severity="info" sx={{ mt: 1 }}>
              To edit templates for this automation, go to the VA Settings page.
            </Alert>
          </Box>
        )}
        <Typography sx={{ mt: 2, mb: 1, fontSize: 16 }}>
          <strong>Pause automation</strong>
        </Typography>
        <FormControlLabel
          label={
            automation.status === AutomationStatus.inactive
              ? "Automation is paused"
              : "Automation is not paused"
          }
          control={
            <Switch
              checked={automation.status === AutomationStatus.inactive}
              onChange={handleStatusToggle}
              color="primary"
            />
          }
        />
        <Typography sx={{ mt: 2, mb: 1, fontSize: 16 }}>
          <strong>Automation Niche</strong>
        </Typography>
        <Grid container>
          <Grid item xs md={6}>
            <FormControl variant="standard" fullWidth>
              <Select
                onChange={handleChange}
                label=""
                value={automation.organizationNicheId || "None"}
                name="organizationNicheId"
              >
                <MenuItem value={"None"}>
                  <em>No niche</em>
                </MenuItem>
                {niches?.map((niche: OrganizationNiche) => (
                  <MenuItem key={niche.id} value={niche.id}>
                    {niche.name}{" "}
                    <Chip
                      size="small"
                      sx={{
                        ml: 1,
                        fontSize: 10,
                        backgroundColor:
                          niche.status === OrganizationNicheStatus.pending
                            ? grey[200]
                            : niche.status ===
                                  OrganizationNicheStatus.canceled ||
                                niche.isArchived
                              ? red[200]
                              : green[200],
                      }}
                      label={niche.isArchived ? "Archived" : niche.status}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid
            item
            xs="auto"
            md={3}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => setCreateNicheDialogOpen(true)}
              sx={{ ml: 1, textTransform: "none" }}
            >
              Create New
            </Button>
            {automation.organizationNicheId && (
              <>
                <IconButton
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={() => setEditNicheDialogOpen(true)}
                >
                  <Box component="i" className="fa-regular fa-edit" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ ml: 0.5, color: "error.main" }}
                  onClick={() => setDeleteNicheDialogOpen(true)}
                >
                  <Box component="i" className="fa-regular fa-trash" />
                </IconButton>
              </>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Grid container item xs={12} justifyContent="space-between">
          <Grid container item xs="auto">
            {automation?.id && automation?.id > 0 && (
              <LoadingButton
                disableElevation
                size="small"
                color="secondary"
                onClick={openRefreshDialog}
                loading={deleteLoading}
              >
                Delete Existing Automation Results
              </LoadingButton>
            )}
          </Grid>

          <Grid container item xs justifyContent="flex-end">
            <Button size="small" color="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              onClick={handleSave}
              loading={loading}
              disableElevation
              variant="contained"
              size="small"
            >
              Save
            </LoadingButton>
          </Grid>
        </Grid>
      </DialogActions>

      <ConfirmDialog
        header={`Are you sure?`}
        subheader={
          "This will delete all unsent saved brands created by this automation (including those in Upcoming Pitches). It will not delete any saved brands saved by the user."
        }
        open={refreshDialogOpen}
        handleClose={() => setRefreshDialogOpen(false)}
        handleConfirm={() => {
          handleRefreshAutomation();
          setRefreshDialogOpen(false);
        }}
      />

      {automation.organizationNicheId && editNicheDialogOpen && (
        <EditAutomationNicheDialog
          open={editNicheDialogOpen}
          handleClose={() => setEditNicheDialogOpen(false)}
          niche={niches.find((n) => n.id === automation.organizationNicheId)!}
          categoriesToPickFrom={categoriesToPickFrom}
          onNicheUpdated={handleNicheUpdated}
        />
      )}

      {createNicheDialogOpen && (
        <CreateNicheDialog
          open={createNicheDialogOpen}
          handleClose={() => setCreateNicheDialogOpen(false)}
          categoriesToPickFrom={categoriesToPickFrom}
          onNicheCreated={handleNicheCreated}
        />
      )}

      <ConfirmDialog
        header="Delete Niche"
        subheader="Are you sure you want to delete this niche? This action cannot be undone and will remove the niche from all automations that use it."
        open={deleteNicheDialogOpen}
        handleClose={() => setDeleteNicheDialogOpen(false)}
        handleConfirm={handleDeleteNiche}
        loading={deleteNicheLoading}
      />
    </Dialog>
  );
};

export default CreateAutomationDialog;
